import { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import { GameProgress } from '../data/types'

interface GameContextType {
  progress: GameProgress
  updateProgress: (wordId: number, correct: boolean) => void
  hydrateProgressFromPlayerData: (data: {
    totalScore: number
    bestStreak: number
    wordsLearned: { wordId: number; correctCount: number; wrongCount: number }[]
    sessionsPlayed: number
    lastPlayed?: string | null
  }) => void
  getScore: () => number
  getStreak: () => number
  resetProgress: () => void
}

const GameContext = createContext<GameContextType | undefined>(undefined)

const STORAGE_KEY = 'hsk2-game-progress'

export function GameProvider({ children }: { children: ReactNode }) {
  const [progress, setProgress] = useState<GameProgress>(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<GameProgress>
      return {
        totalScore: parsed.totalScore || 0,
        streak: parsed.streak || 0,
        maxStreak: parsed.maxStreak || 0,
        wordsLearned: parsed.wordsLearned || [],
        correctAnswers: parsed.correctAnswers || {},
        wordStats: parsed.wordStats || {},
        gamesPlayed: parsed.gamesPlayed || 0,
        lastPlayed: parsed.lastPlayed || null,
      }
    }
    return {
      totalScore: 0,
      streak: 0,
      maxStreak: 0,
      wordsLearned: [],
      correctAnswers: {},
      wordStats: {},
      gamesPlayed: 0,
      lastPlayed: null,
    }
  })

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress))
  }, [progress])

  const updateProgress = (wordId: number, correct: boolean) => {
    setProgress(prev => {
      const newCorrect = { ...prev.correctAnswers }
      const existingStat = prev.wordStats[wordId] || { correct: 0, wrong: 0 }
      const newWordStats = {
        ...prev.wordStats,
        [wordId]: correct
          ? { ...existingStat, correct: existingStat.correct + 1 }
          : { ...existingStat, wrong: existingStat.wrong + 1 },
      }

      if (correct) {
        newCorrect[wordId] = (newCorrect[wordId] || 0) + 1
        return {
          ...prev,
          totalScore: prev.totalScore + 10,
          streak: prev.streak + 1,
          maxStreak: Math.max(prev.maxStreak, prev.streak + 1),
          wordsLearned: prev.wordsLearned.includes(wordId) ? prev.wordsLearned : [...prev.wordsLearned, wordId],
          correctAnswers: newCorrect,
          wordStats: newWordStats,
          gamesPlayed: prev.gamesPlayed + 1,
          lastPlayed: new Date().toISOString(),
        }
      }

      return {
        ...prev,
        streak: 0,
        wordStats: newWordStats,
        gamesPlayed: prev.gamesPlayed + 1,
        lastPlayed: new Date().toISOString(),
      }
    })
  }

  const hydrateProgressFromPlayerData = (data: {
    totalScore: number
    bestStreak: number
    wordsLearned: { wordId: number; correctCount: number; wrongCount: number }[]
    sessionsPlayed: number
    lastPlayed?: string | null
  }) => {
    const wordStats = data.wordsLearned.reduce<Record<number, { correct: number; wrong: number }>>((acc, w) => {
      acc[w.wordId] = { correct: w.correctCount || 0, wrong: w.wrongCount || 0 }
      return acc
    }, {})

    const correctAnswers = data.wordsLearned.reduce<Record<number, number>>((acc, w) => {
      acc[w.wordId] = w.correctCount || 0
      return acc
    }, {})

    setProgress(prev => ({
      ...prev,
      totalScore: data.totalScore || 0,
      maxStreak: data.bestStreak || 0,
      streak: 0,
      wordsLearned: data.wordsLearned.map(w => w.wordId),
      correctAnswers,
      wordStats,
      gamesPlayed: data.sessionsPlayed || 0,
      lastPlayed: data.lastPlayed || prev.lastPlayed,
    }))
  }

  const getScore = () => progress.totalScore
  const getStreak = () => progress.streak
  const resetProgress = () => {
    setProgress({
      totalScore: 0,
      streak: 0,
      maxStreak: 0,
      wordsLearned: [],
      correctAnswers: {},
      wordStats: {},
      gamesPlayed: 0,
      lastPlayed: null,
    })
  }

  return (
    <GameContext.Provider value={{ progress, updateProgress, hydrateProgressFromPlayerData, getScore, getStreak, resetProgress }}>
      {children}
    </GameContext.Provider>
  )
}

export function useGame() {
  const context = useContext(GameContext)
  if (!context) {
    throw new Error('useGame must be used within a GameProvider')
  }
  return context
}
